import { Injectable } from '@angular/core';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { WebSocketNotificationTypeEnum } from 'src/app/core/websocket';
import { WebSocketService } from 'src/app/core/websocket/websocket.service';
import { API_URL_COMPANIES } from 'src/app/shared/services/basic.service';
import { AIAssistantAnswer } from 'src/app/task-process/shared/models/ai-assistant-answer.model';

import { AiSummaryStatusEnum } from './enums';

@Injectable({
  providedIn: 'root',
})
export class AiSummaryService {
  public summaryPending: {
    [key: number]: {
      resultBS: BehaviorSubject<AIAssistantAnswer>;
      expiredSubscription?: Subscription; // after 60 seconds if any messages were received consider there was a timeout.
      received?: boolean; // In case of success or WS / IA / serveur issue, allows to consider the message was received and allows the user to recall the Ia is needed.
    };
  } = {};

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private asyncRequestService: AsyncRequestService,
    private webSocketService: WebSocketService
  ) {}

  getAiSummary(taskId: number): void {
    this.summaryPending[taskId] = {
      resultBS: new BehaviorSubject(null),
      expiredSubscription: new Subscription(),
      received: false,
    };

    this.configureAndAttachAnExpirationToSummary(taskId);

    this.asyncRequestService
      .post(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/task/${taskId}/summary`, null)
      .subscribe({
        next: () => {
          this.webSocketService.message$.next({
            type: WebSocketNotificationTypeEnum.AIAssistantSummary,
            data: { status: AiSummaryStatusEnum.STARTED },
            task_id: taskId,
          } as any);
        },
        error: () => {
          this.webSocketService.message$.next({
            type: WebSocketNotificationTypeEnum.AIAssistantSummary,
            data: { status: AiSummaryStatusEnum.FAILED },
            task_id: taskId,
          } as any);
        },
      });
  }

  private configureAndAttachAnExpirationToSummary(taskId: number) {
    const jwtValidityInMs = 60000;
    this.summaryPending[taskId].expiredSubscription = of(1)
      .pipe(
        delay(jwtValidityInMs),
        tap(() => {
          if (this.summaryPending[taskId] && !this.summaryPending[taskId].received) {
            this.webSocketService.message$.next({
              type: WebSocketNotificationTypeEnum.AIAssistantSummary,
              data: { status: AiSummaryStatusEnum.EXPIRED },
              task_id: taskId,
            } as any);
          }
        })
      )
      .subscribe();
  }
}
